import Joi from 'joi'
import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import { TApi } from '../../../../../../shared/const'
import http from '../../../../../../shared/http'
import useDebouncedValue from '../../../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../../../shared/useLatestOnly'
import Btn from '../../../../../../UI/Btn'
import TextField from '../../../../../../UI/TextField'

import SettingHint from '../SettingHint'
import TemplateLinks from '../TemplateLinks'

import styles from '../style.module.scss'

/*
 * Die Grenzen stehen genauso im Backend (readReminderConfig in
 * services/supporter/invitationSchedule.js). Die erste Erinnerung muss vor dem
 * Ablauf der Einladung (60 Tage) liegen, sonst kaeme sie nie.
 */
const tage = (min, max, wort) =>
	Joi.number()
		.integer()
		.min(min)
		.max(max)
		.required()
		.messages({
			'number.base': `Bitte ${wort} in Tagen angeben.`,
			'number.integer': `Bitte ${wort} in ganzen Tagen angeben.`,
			'number.min': `Bitte mindestens ${min} angeben.`,
			'number.max': `Bitte höchstens ${max} angeben.`,
			'any.required': `Bitte ${wort} in Tagen angeben.`
		})

// Grenzen wie im Backend (invitationSchedule: INVITATION_VALID_DAYS_MIN/MAX).
export const VALID_DAYS_MIN = 14
export const VALID_DAYS_MAX = 90

const schema = Joi.object({
	supporterInvitationReminders: Joi.object().keys({
		validDays: tage(VALID_DAYS_MIN, VALID_DAYS_MAX, 'eine Gültigkeit'),
		dueDays: tage(1, VALID_DAYS_MAX - 1, 'eine Frist')
			.less(Joi.ref('validDays'))
			.messages({ 'number.less': 'Die erste Erinnerung muss vor dem Ablauf der Einladung liegen.' }),
		frequencyDays: tage(1, VALID_DAYS_MAX, 'einen Takt'),
		maxCount: Joi.number().integer().min(1).max(10).required().messages({
			'number.base': 'Bitte eine Anzahl angeben.',
			'number.integer': 'Bitte eine ganze Zahl angeben.',
			'number.min': 'Bitte mindestens 1 angeben.',
			'number.max': 'Bitte höchstens 10 angeben.',
			'any.required': 'Bitte eine Anzahl angeben.'
		})
	})
})

/**
 * Erinnerung an Einladungen, die noch niemand angenommen hat.
 *
 * Bis 31.10.2026 ging nach der Einladung nichts mehr hinaus; wer den Link
 * übersah, hörte nie wieder etwas. Der Lauf erinnert die eingeladene Person
 * selbst - nicht die Verwaltung.
 */
/** Ein Satz zur Wirkung auf die offenen Einladungen - ohne Fachbegriffe. */
export const impactSentence = impact => {
	const wort = n => (n === 1 ? 'Einladung' : 'Einladungen')
	const offen =
		impact.open === 0
			? 'Derzeit ist keine Einladung offen.'
			: `Offen ${impact.open === 1 ? 'ist' : 'sind'} ${impact.open} ${wort(impact.open)}, davon ${
					impact.validNow
			  } noch gültig.`
	if (impact.nextDays === impact.currentDays) return `${offen} Gespeichert sind ${impact.currentDays} Tage.`
	const folgen = []
	const verb = n => (n === 1 ? 'würde' : 'würden')
	if (impact.becomeExpired) {
		folgen.push(`${impact.becomeExpired} ${wort(impact.becomeExpired)} ${verb(impact.becomeExpired)} sofort ungültig`)
	}
	if (impact.becomeValid) {
		folgen.push(
			`${impact.becomeValid} abgelaufene ${wort(impact.becomeValid)} ${verb(impact.becomeValid)} wieder gültig`
		)
	}
	return `${offen} Mit ${impact.nextDays} statt ${impact.currentDays} Tagen: ${
		folgen.length ? folgen.join(', ') : 'keine offene Einladung wäre betroffen'
	}.`
}

export default function SupporterInvitationReminders(props) {
	const { t } = useTranslation()
	const {
		register,
		reset,
		handleSubmit,
		watch,
		formState: { errors }
	} = useForm({ resolver: joiResolver(schema) })

	/*
	 * Die Frist zählt ab dem Versand und gilt sofort für alle offenen
	 * Einladungen. Was eine Änderung mit ihnen machte, rechnet der Server -
	 * hier steht es, bevor gespeichert wird.
	 */
	const typedDays = Number(watch('supporterInvitationReminders.validDays'))
	const debouncedDays = useDebouncedValue(typedDays)
	const [impact, setImpact] = useState(null)
	// Nach dem Speichern ist der gespeicherte Wert ein anderer - neu rechnen.
	const [saved, setSaved] = useState(0)
	const latest = useLatestOnly()

	useEffect(() => {
		if (!Number.isInteger(debouncedDays) || debouncedDays < VALID_DAYS_MIN || debouncedDays > VALID_DAYS_MAX) {
			return
		}
		const isCurrent = latest()
		http.get(TApi.ADMIN_SUPPORTER_INVITATION_IMPACT, {
			params: { validDays: debouncedDays },
			disableToast: true
		})
			.then(({ data }) => isCurrent() && setImpact(data))
			.catch(() => isCurrent() && setImpact(null))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedDays, saved])

	const onSubmit = form => {
		props
			.updateOptions({
				name: 'supporterInvitationReminders',
				// „enabled" steht nicht im Formular: Abschalten geht über die
				// Vorlage unter „Kommunikation". Der vorhandene Wert bleibt.
				value: {
					...(props.form?.supporterInvitationReminders || {}),
					validDays: Number(form.supporterInvitationReminders.validDays),
					dueDays: Number(form.supporterInvitationReminders.dueDays),
					frequencyDays: Number(form.supporterInvitationReminders.frequencyDays),
					maxCount: Number(form.supporterInvitationReminders.maxCount)
				}
			})
			.then(() => {
				toast.success(t('Options was updated'))
				setSaved(n => n + 1)
			})
	}

	useEffect(() => {
		const value = props.form?.supporterInvitationReminders || {}
		reset({
			supporterInvitationReminders: {
				validDays: value.validDays ?? 60,
				dueDays: value.dueDays ?? 7,
				frequencyDays: value.frequencyDays ?? 14,
				maxCount: value.maxCount ?? 3
			}
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="supporterInvitationReminders" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>Erinnerung an offene Einladungen</h3>
				<SettingHint
					example="Beispiel: nach 7 Tagen erinnern, danach alle 14 Tage, höchstens dreimal — die letzte Erinnerung kommt dann nach 35 Tagen, gut drei Wochen vor dem Ablauf."
					warning="Die Gültigkeit zählt ab dem Versand und gilt sofort für alle offenen Einladungen: Wer sie kürzt, macht ältere Links sofort ungültig, wer sie verlängert, macht abgelaufene wieder gültig. Danach wird nicht mehr erinnert. „Einladung erneut senden“ verschickt einen neuen Link, Frist und Erinnerungen beginnen dann von vorn."
				>
					Wer als Unterstützer:in eingeladen wird — von der Verwaltung oder von einer Junior Company — und sein
					Profil nicht aktiviert, bekommt nach der hier eingestellten Frist eine Erinnerung. Der Lauf findet
					einmal täglich in der Nacht statt.
				</SettingHint>
				<TemplateLinks items={[{ channel: 'email', key: 'SUPPORTER_INVITE_REMINDER' }]} />
				<div className={styles.sectionContent}>
					<div className={styles.flexContainer}>
						<TextField
							className={styles.field}
							label={`Einladung gilt [x] Tage (${VALID_DAYS_MIN}–${VALID_DAYS_MAX})`}
							mb={20}
							register={register('supporterInvitationReminders.validDays')}
							error={errors.supporterInvitationReminders?.validDays}
						/>
						<TextField
							className={styles.field}
							label="Erste Erinnerung nach [x] Tagen"
							mb={20}
							register={register('supporterInvitationReminders.dueDays')}
							error={errors.supporterInvitationReminders?.dueDays}
						/>
						<TextField
							className={styles.field}
							label="Danach erneut erinnern alle [x] Tage"
							mb={20}
							register={register('supporterInvitationReminders.frequencyDays')}
							error={errors.supporterInvitationReminders?.frequencyDays}
						/>
						<TextField
							className={styles.field}
							label="Höchstens [x] Erinnerungen"
							mb={20}
							register={register('supporterInvitationReminders.maxCount')}
							error={errors.supporterInvitationReminders?.maxCount}
						/>
					</div>
					{impact && (
						<p className={styles.impact} data-testid="invitation-impact">
							{impactSentence(impact)}
						</p>
					)}
					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

SupporterInvitationReminders.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
